
import KeyenceHTHardKeySerialNumberButton from '@/components/keyenceHT/hardkeys/KeyenceHTHardKeySerialNumberButton.vue'
import { LOCAL_STORAGE_ITEM, getLocalStorageKey } from '@/utils/constants'
import { logError } from '@/utils/errorLogger'
import { objectToArray, sortTrackingPoints } from '@/utils/helpers'
import { isKeyenceEnterpriseBrowser } from '@/utils/keyenceHT/keyenceModules'
import { unsubmittedSerialCodes } from '@/utils/serialChanges'
import { IBarcode } from '@/utils/types/barcodeTypes'
import { IBarcodeDefinitionType } from '@/utils/types/fieldDefinition'
import { ITrackpoint, ITrackPointKeyVal } from '@/utils/types/projectTypes'
import cloneDeep from 'lodash/cloneDeep'
import { mixins, Options } from 'vue-class-component'
import { Prop } from 'vue-property-decorator'
import BarcodeTypesMixin from '../mixins/BarcodeTypesMixin.vue'
import KeyenceHTHardKeyButton from '@/components/keyenceHT/hardkeys/KeyenceHTHardKeyButton.vue'
import { KeyenceHardKey } from '@/utils/keyenceHT/keyenceHardKeyEventHandler'
import { IListStringItem } from '@/utils/types/commonTypes'
import { GET_TRACKING, OPEN_DIALOG } from '@/store/actions'

@Options({
  name: 'TrackedDataFlowMarumiya',
  components: {
    KeyenceHTHardKeySerialNumberButton,
    KeyenceHTHardKeyButton,
  },
})
export default class TrackedDataFlowMarumiya extends mixins(BarcodeTypesMixin) {
  @Prop({ type: Object }) readonly barcode?: IBarcode
  @Prop({ type: Object }) readonly arrTrackPoints?: ITrackPointKeyVal[]
  @Prop({ type: Number, default: 2 }) readonly startHardKeyNumber?: number

  componentKey = 0
  trackingPointDatas: { [key: string]: ITrackpoint } = {}
  isOpenWorkCenterError = false
  workCenterErrorMessage = ''
  specialTrackingPointNames = ['残回収', '完了']
  loadingTrackPointKey: string | null = null
  KeyenceHardKey = KeyenceHardKey

  get isKeyenceEnterpriseBrowser() {
    return isKeyenceEnterpriseBrowser()
  }

  getHardKeyNumber(index: number): number {
    return (this.startHardKeyNumber || 2) + index
  }

  get getArrTrackPoints(): ITrackPointKeyVal[] {
    if (!this.barcode && this.arrTrackPoints && this.arrTrackPoints.length > 0) {
      return this.arrTrackPoints
    }

    // If no current tracking point, return empty array
    if (!this.currentNode) {
      return []
    }
    const bcType: IBarcodeDefinitionType = this.currentBarcodeTypeInfo(this.barcode)
    this.trackingPointDatas = cloneDeep(this.$store.state.project?.details.trackPoints)
    const arrTrackPoints = objectToArray<ITrackpoint>(this.trackingPointDatas) || []
    sortTrackingPoints(arrTrackPoints)

    // Find the current tracking point
    const currentTrackPoint = arrTrackPoints.find((tp) => tp.key === this.currentNode)
    if (!currentTrackPoint) {
      return []
    }

    // Get the tracking points that current point can go to
    const trackingPointForms = currentTrackPoint.value.trackPointForms || {}
    const nextTrackPointKeys = Object.keys(trackingPointForms)

    // Filter to only show the next available tracking points
    const nextTrackPoints = arrTrackPoints.filter((tp) => nextTrackPointKeys.includes(tp.key))

    try {
      const restrictedRoutes = bcType.trackPointRouteRestrictedPaths ?? []

      // Filter out restricted routes and check permissions
      const filteredNextTrackPoints = nextTrackPoints.filter((tp) => {
        const isRestricted = restrictedRoutes.find((rr) => rr.from === this.currentNode && rr.to === tp.key)
        const isWritable = this.currentNode
          ? this.$store.getters.projectPermission?.trackingPermissions?.[this.currentNode]?.canEdit
          : false
        const isWritableNextTrackingPoint = this.currentNode
          ? this.$store.getters.projectPermission?.nextTrackingPointPermissions?.[tp.key]?.canEdit
          : false
        return !isRestricted && isWritable && isWritableNextTrackingPoint
      })

      return filteredNextTrackPoints
    } catch (e) {
      return nextTrackPoints
    }
  }

  get currentNode() {
    return this.barcode?.currentTrackPointKey
  }

  get highlightedColor() {
    return '#2563eb'
  }

  drawLeaderLine() {
    // No line drawing needed - method kept for compatibility
    return Promise.resolve()
  }

  scannedWorkCenterCode() {
    return localStorage.getItem(LOCAL_STORAGE_ITEM.WORK_CENTER_CODE)
  }

  get currentManufacturingWRKC() {
    return this.barcode?.manufacturingPlan?.workCenterCode || ''
  }

  async onTrackingPointClick(node: ITrackPointKeyVal) {
    const isSpecialNode = node.value.name && this.specialTrackingPointNames.includes(node.value.name)

    if (!isSpecialNode && !this.scannedWorkCenterCode()) {
      this.workCenterErrorMessage = 'work_center_code_required'
      logError('', {}, '', this.$t('work_center_code_required'))
      this.isOpenWorkCenterError = true
      return
    }

    if (!this.scannedWorkCenterCode()?.includes(this.currentManufacturingWRKC)) {
      // Proceed to tracking
      this.workCenterErrorMessage = 'work_center_code_mismatch'
      logError('', {}, '', this.$t('work_center_code_mismatch'))
      this.isOpenWorkCenterError = true
      return
    }
    this.loadingTrackPointKey = node.key
    let unsubmittedTrackPointKey: string | null = null
    try {
      unsubmittedTrackPointKey = await this.findUnsubmittedTrackPoint(node.key)
    } finally {
      this.loadingTrackPointKey = null
    }
    if (unsubmittedTrackPointKey) {
      this.openUnsubmittedSerialsWarningDialog(unsubmittedTrackPointKey)
      return
    }

    this.goToTrackingForm(node.key)
  }

  goToTrackingForm(trackPointKey: string) {
    this.$router.push({
      name: 'track',
      params: {
        project: this.$store.getters.projectParam,
        barcodeId: this.barcode?.id,
        selectedTarget: trackPointKey,
      },
    })
  }

  keyPreviousTrackingDataDraft(trackingPointKey: number): string {
    if (!this.project || !this.barcode) return ''

    return getLocalStorageKey.trackingDataDraft(this.project.code, this.barcode.id, `${trackingPointKey}`)
  }

  async findUnsubmittedTrackPoint(excludeTrackPointKey?: string): Promise<string | null> {
    const trackPointKeys = [1, 2, 3].filter((key) => `${key}` !== excludeTrackPointKey)
    for (const trackPointKey of trackPointKeys) {
      const codes = await this.getUnsubmittedSerialCodesByTrackPoint(trackPointKey)
      if (codes.length > 0) {
        return `${trackPointKey}`
      }
    }
    return null
  }

  getUnsubmittedWarningTitleKey(trackPointKey: string): string {
    const trackPoints = objectToArray<ITrackpoint>(cloneDeep(this.$store.state.project?.details.trackPoints)) || []
    const name = trackPoints.find((tp) => tp.key === trackPointKey)?.value.name || ''
    if (name.includes('使用')) return 'unsubmitted_serial_warning_title_use'
    if (name.includes('製造')) return 'unsubmitted_serial_warning_title_produce'
    if (name.includes('残')) return 'unsubmitted_serial_warning_title_left_amount'
    return 'unsubmitted_serial_warning_title'
  }

  async getUnsubmittedSerialCodesByTrackPoint(trackPointKey: number): Promise<string[]> {
    const localRaw = localStorage.getItem(this.keyPreviousTrackingDataDraft(trackPointKey))
    if (!localRaw) return []
    let localProducedSerial
    try {
      localProducedSerial = JSON.parse(localRaw)
    } catch (error) {
      return []
    }
    if (!localProducedSerial) return []
    const localSerial = (localProducedSerial.sirial as IListStringItem[]) || []
    const removedSerial = (localProducedSerial.removed_sirial as IListStringItem[]) || []
    if (localSerial.length === 0 && removedSerial.length === 0) return []

    let apiSerial: IListStringItem[] = []
    try {
      const trackingData = await this.$store.dispatch(GET_TRACKING, {
        barcodeId: this.barcode?.id || '',
        nextTrackPointKey: trackPointKey,
      })
      apiSerial = (trackingData?.customFields?.sirial?.listString as IListStringItem[]) || []
    } catch (error) {
      apiSerial = []
    }
    return unsubmittedSerialCodes(apiSerial, localSerial, removedSerial)
  }

  openUnsubmittedSerialsWarningDialog(trackPointKey: string) {
    const titleKey = this.getUnsubmittedWarningTitleKey(trackPointKey)
    this.$store.dispatch(OPEN_DIALOG, {
      message: `<div style="font-weight: bold; text-align: center;">
        <div style="background-color: red; color: white; margin: 10px 0; padding: 5px;">
          ${this.$t(titleKey)}
        </div>
        <div style="color: red;">
          <div>${this.$t('unsubmitted_serial_warning_message')}</div>
        </div>
      </div>`,
      onConfirm: () => this.goToTrackingForm(trackPointKey),
    })
  }
}
